// ============================================================
// ImportCasesModal.tsx
// Generic CSV importer with MIXED imports (existing + new clients in one
// file) via per-row client matching. Flow:
//   upload → map columns → MATCH CLIENTS → preview/validate → import
//
// Per row, the mapped forename+surname is matched against the firm's
// existing clients (matchRowsToClients — swappable for a future AI
// layer). Each row resolves to LINK (attach the case to an existing
// client) or CREATE (make a new client). The user reviews + can flip any
// row; ambiguous names (>1 existing match) are NEVER auto-linked.
//
// Import is BEST-EFFORT (a failed row doesn't abort the rest). An
// "Assign imported cases to me" toggle (default OFF) controls whether the
// creator-assignee that createCaseApi sets is kept or cleared.
// B2: every case needs a claim type — the review step asks for ONE claim
// type (+ route) for the whole file (ClaimTypeFields); Import waits for it.
// ============================================================
import ActionBox from './ActionBox'
import React, { useMemo, useState } from 'react'
import { parseCsv, type ParsedCsv } from '../../utils/parseCsv'
import {
  CLIENT_IMPORT_FIELDS, CASE_IMPORT_FIELDS, autoMapHeaders, coerceValue, importDateRuleIssues, type Century,
  type ImportField, otherInjuryIssue,
} from '../../utils/importFields'
import { matchRowsToClients, type RowResolution } from '../../utils/matchClients'
import { listClientsApi, importCasesApi, ImportFailedError, type ImportCaseRow } from '../../api/casevoApi'
import ClaimTypeFields, { EMPTY_CLAIM_CHOICE, type ClaimChoice } from '../cases/ClaimTypeFields'

import { useCasevoStore } from '../../store/useCasevoStore'
import type { CreateClientPayload, Client } from '../../types/client.types'
import ClientPickerModal from './ClientPickerModal'
import ModalFrame, { HERO_PILL_SOFT } from './ModalFrame'
import { BTN_PRIMARY } from '../../styles/buttons'

// The same words as the server's up-front check (caseImportController).
export const IMPORT_CLAIM_TYPE_MISSING = 'Choose a claim type for this import.'

interface ImportCasesModalProps {
  isOpen: boolean
  onClose: () => void
  onImported: () => void
}

type Step = 'upload' | 'map' | 'match' | 'preview' | 'importing' | 'done'

// Base CreateClientPayload with every required key defaulted, so mapped
// values can be spread over it and always satisfy the type + backend.
const emptyClientPayload = (): CreateClientPayload => ({
  title: null, forename: '', surname: '',
  addressLine1: '', addressLine2: '', addressTown: '', addressCounty: '', addressPostcode: '',
  dateOfBirth: null, hasNiNumber: null, niNumber: '',
  mainContactNumber: '', emailAddress: '', employmentStatus: null, occupation: '',
})

// Field catalogs are static now (no mode split) — client + case fields
// are always available for mapping. Module-level so the lookup is stable.
const ALL_FIELDS: readonly ImportField[] = [...CLIENT_IMPORT_FIELDS, ...CASE_IMPORT_FIELDS]
const FIELD_BY_KEY = new Map<string, ImportField>(ALL_FIELDS.map((f) => [f.key, f]))
const CLIENT_KEYS = new Set(CLIENT_IMPORT_FIELDS.map((f) => f.key))

interface BuiltRow {
  clientPayload: CreateClientPayload // person fields (used when creating)
  caseUpdates: Record<string, unknown>
  forename: string
  surname: string
  fieldErrors: string[] // coercion errors (bad date/number/boolean) + date-rule BLOCKS
  ambiguous: { key: string; label: string; sample: string }[] // 2-digit years awaiting a century
}

const ImportCasesModal: React.FC<ImportCasesModalProps> = ({ isOpen, onClose, onImported }) => {
  const [step, setStep] = useState<Step>('upload')
  const [parsed, setParsed] = useState<ParsedCsv | null>(null)
  const [fileName, setFileName] = useState('')
  const [parseError, setParseError] = useState<string | null>(null)
  const [mapping, setMapping] = useState<Record<number, string>>({})
  // Two-digit years: the century chosen per DATE COLUMN on the review step
  // ("27" → 1927 or 2027 — never guessed; rows wait until it's chosen).
  const [centuries, setCenturies] = useState<Record<string, Century>>({})
  const [clients, setClients] = useState<Client[]>([])
  const [clientsLoading, setClientsLoading] = useState(false)
  // Set when the client list can't be loaded — matching MUST NOT proceed
  // against an empty list (every row would resolve to 'create' → duplicate
  // clients). We surface a retry instead.
  const [matchError, setMatchError] = useState<string | null>(null)
  const [resolutions, setResolutions] = useState<RowResolution[]>([])
  // ★ Leaving imported cases UNASSIGNED hides them from the importer unless
  // they can see every case (admin / seeAllCases): an unassigned, non-firm-
  // wide case has no audience. So the default is "assign to me", and the
  // option to leave them unassigned is only offered to users who'd still
  // see them.
  const authUser = useCasevoStore((s) => s.authUser)
  const canLeaveUnassigned = authUser?.role === 'admin' || authUser?.effectivePermissions?.seeAllCases === true
  const [assignToMe, setAssignToMe] = useState(true)
  const [claim, setClaim] = useState<ClaimChoice>(EMPTY_CLAIM_CHOICE)
  const [claimError, setClaimError] = useState<string | null>(null)
  const [manualPickRow, setManualPickRow] = useState<number | null>(null)
  // failedRow: the import was rolled back (nothing created) — the row that
  // failed and why (row null = a server/network failure).
  const [results, setResults] = useState<{ success: number; failures: { row: number; error: string }[]; failedRow: { row: number | null; reason: string } | null } | null>(null)
  const [progress, setProgress] = useState(0)

  const reset = (): void => {
    setStep('upload'); setParsed(null); setFileName(''); setParseError(null)
    setMapping({}); setClients([]); setClientsLoading(false); setMatchError(null)
    setResolutions([]); setAssignToMe(true); setClaim(EMPTY_CLAIM_CHOICE); setClaimError(null)
    setManualPickRow(null); setResults(null); setProgress(0)
  }
  // Closing mid-import would reset the UI while the loop keeps creating
  // records in the background — blocked while importing.
  const handleClose = (): void => { if (step === 'importing') return; reset(); onClose() }

  // ── STEP 1: upload + parse ──
  const handleFile = async (file: File): Promise<void> => {
    setParseError(null)
    try {
      // Excel "CSV (Comma delimited)" on UK Windows saves Windows-1252, not
      // UTF-8 — decoding as UTF-8 turned £ / é into U+FFFD. Try strict UTF-8,
      // fall back to Windows-1252.
      const buf = await file.arrayBuffer()
      let text: string
      try { text = new TextDecoder('utf-8', { fatal: true }).decode(buf) } catch { text = new TextDecoder('windows-1252').decode(buf) }
      const p = parseCsv(text)
      if (p.unterminatedQuote) {
        setParseError('That file has an unclosed quote (") — part of it would be read as one cell. Please check the file and re-export it.')
        return
      }
      if (p.headers.length === 0 || p.rows.length === 0) {
        setParseError('That file has no data rows. Expected a header row plus at least one row.')
        return
      }
      setParsed(p)
      setFileName(file.name)
      setMapping(autoMapHeaders(p.headers, ALL_FIELDS))
      setStep('map')
    } catch {
      setParseError('Could not read that file. Please upload a .csv file.')
    }
  }

  // ── Build one row's payload from the current mapping ──
  const buildRow = (cells: string[]): BuiltRow => {
    const fieldErrors: string[] = []
    const ambiguous: BuiltRow['ambiguous'] = []
    const clientPayload = emptyClientPayload()
    const caseUpdates: Record<string, unknown> = {}
    for (const [idxStr, key] of Object.entries(mapping)) {
      if (!key) continue
      const field = FIELD_BY_KEY.get(key)
      if (!field) continue
      const raw = cells[Number(idxStr)] ?? ''
      const c = coerceValue(field.type, raw, field, { century: centuries[key] })
      if (c.status === 'empty') continue
      if (c.status === 'error') { fieldErrors.push(`${field.label}: ${c.message}`); continue }
      if (c.status === 'ambiguous') { ambiguous.push({ key, label: field.label, sample: raw.trim() }); fieldErrors.push(`${field.label}: ${c.message}`); continue }
      if (CLIENT_KEYS.has(key)) (clientPayload as Record<string, unknown>)[key] = c.value
      else caseUpdates[key] = c.value
    }
    // An imported NI number means the client HAS one (the section's tri-state).
    if (clientPayload.niNumber && String(clientPayload.niNumber).trim() !== '') clientPayload.hasNiNumber = true
    // Date rules are applied per row in dateRulesFor() below — they depend on
    // WHICH client the row ends up with (a linked row uses that client's DOB).
    return { clientPayload, caseUpdates, forename: clientPayload.forename, surname: clientPayload.surname, fieldErrors, ambiguous }
  }

  const builtRows = useMemo(
    () => (parsed ? parsed.rows.map(buildRow) : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [parsed, mapping, centuries],
  )
  // Date columns holding 2-digit years (one example each) — asked on review.
  const ambiguousColumns = useMemo(() => {
    const m = new Map<string, { key: string; label: string; sample: string; rows: number }>()
    for (const r of builtRows) for (const a of r.ambiguous) {
      const hit = m.get(a.key)
      if (hit) hit.rows++
      else m.set(a.key, { ...a, rows: 1 })
    }
    return Array.from(m.values())
  }, [builtRows])
  const mappedCount = Object.values(mapping).filter(Boolean).length

  // ── Enter the MATCH step: fetch the firm's clients FRESH, then match ──
  // Always refetches (a client created since the modal opened must be
  // seen). CRUCIALLY: if the fetch fails we do NOT match against an empty
  // list — that would silently resolve every row to 'create' and produce
  // duplicate clients. Instead we surface matchError + a retry, and the
  // "Next: preview" button stays disabled until clients actually loaded.
  const goToMatch = async (): Promise<void> => {
    setStep('match')
    setMatchError(null)
    setClientsLoading(true)
    try {
      const list = await listClientsApi()
      setClients(list)
      setResolutions(matchRowsToClients(builtRows.map((r) => ({ forename: r.forename, surname: r.surname, dateOfBirth: r.clientPayload.dateOfBirth, niNumber: r.clientPayload.niNumber })), list))
    } catch {
      setResolutions([])
      setMatchError('Could not load existing clients to match against. Retry before continuing — otherwise every row would create a new client.')
    } finally {
      setClientsLoading(false)
    }
  }

  // Update a single row's resolution (user override in the review list).
  const setResolution = (rowIndex: number, patch: Partial<RowResolution>): void => {
    setResolutions((prev) => prev.map((r) => (r.rowIndex === rowIndex ? { ...r, ...patch } : r)))
  }
  const clientName = (c: Client): string => `${c.forename} ${c.surname}`.trim() || `Client #${c.clientNumber}`

  // ── Per-row validation (combines coercion errors + name requirement) ──
  // A LINK row needs no name (client comes from the picked client); a
  // CREATE row needs a forename or surname (can't create a nameless client).
  // The same date rules as editing the fields by hand. The DOB checked is the
  // one the new case will actually get: a LINKED row copies the existing
  // client's DOB (the server's case snapshot), so the CSV's DOB is ignored;
  // a CREATE row uses the CSV's.
  const dateRulesFor = (i: number): { errors: string[]; warnings: string[] } => {
    const r = builtRows[i]
    if (!r) return { errors: [], warnings: [] }
    const res = resolutions[i]
    const linked = res && res.action === 'link' && res.clientId ? clients.find((c) => c.id === res.clientId) : undefined
    const dob = res && res.action === 'link' ? (linked?.dateOfBirth ?? null) : r.clientPayload.dateOfBirth
    return importDateRuleIssues(dob, r.caseUpdates)
  }
  const warningRows = builtRows.filter((_, i) => dateRulesFor(i).warnings.length > 0).length
  const rowError = (i: number): string[] => {
    const other = builtRows[i] ? otherInjuryIssue(builtRows[i].caseUpdates) : null
    const errs = [...(builtRows[i]?.fieldErrors ?? []), ...dateRulesFor(i).errors, ...(other ? [other] : [])]
    const res = resolutions[i]
    if (res && res.action === 'create' && !builtRows[i].forename.trim() && !builtRows[i].surname.trim()) {
      errs.push('Missing client forename/surname')
    }
    return errs
  }
  const validIndexes = useMemo(
    () => builtRows.map((_, i) => i).filter((i) => rowError(i).length === 0),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [builtRows, resolutions],
  )
  const counts = useMemo(() => {
    let create = 0, link = 0
    for (const i of validIndexes) {
      if (resolutions[i]?.action === 'link') link++
      else create++
    }
    return { create, link, total: validIndexes.length }
  }, [validIndexes, resolutions])

  // ── IMPORT — ALL OR NOTHING ──
  // Every importable row goes to the server in ONE request (POST
  // /api/cases/import), which creates them all inside one transaction with the
  // same rules as creating a case by hand: if any row fails, NOTHING is
  // created and the reply names that row. Rows with problems on the review
  // step are left out (and listed); the rest import together or not at all.
  const runImport = async (): Promise<void> => {
    if (!parsed) return
    if (!claim.claimType) { setClaimError(IMPORT_CLAIM_TYPE_MISSING); return }
    const claimType = claim.claimType
    setStep('importing')
    setProgress(0)
    const skipped: { row: number; error: string }[] = []
    // New clients are keyed by name + DOB so the same new person on two rows
    // gets one client (the server reuses the first one it creates).
    const runKey = (p: CreateClientPayload): string =>
      `${(p.forename ?? '').trim().toLowerCase()}|${(p.surname ?? '').trim().toLowerCase()}|${p.dateOfBirth ?? ''}`
    const rows: ImportCaseRow[] = []
    for (let i = 0; i < parsed.rows.length; i++) {
      const errs = rowError(i)
      if (errs.length > 0) { skipped.push({ row: i + 2, error: `Not imported — ${errs.join('; ')}` }); continue }
      const built = builtRows[i]
      const res = resolutions[i]
      rows.push({
        row: i + 2,
        client: res.action === 'link'
          ? { linkClientId: res.clientId ?? '' }
          : { create: built.clientPayload as unknown as Record<string, unknown>, key: runKey(built.clientPayload) },
        claimType,
        claimRoute: claim.claimRoute || null,
        caseUpdates: built.caseUpdates as unknown as Record<string, unknown>,
        // createCase assigns the case to the creator; clear it only when the
        // user chose to (offered only when they'd still see the case).
        leaveUnassigned: !assignToMe && canLeaveUnassigned,
      })
    }
    try {
      const { imported } = await importCasesApi(rows)
      setProgress(parsed.rows.length)
      setResults({ success: imported, failures: skipped, failedRow: null })
      setStep('done')
      if (imported > 0) onImported()
    } catch (err) {
      const failed = err instanceof ImportFailedError ? { row: err.row, reason: err.reason } : { row: null, reason: err instanceof Error ? err.message : 'The import failed.' }
      setResults({ success: 0, failures: skipped, failedRow: failed })
      setStep('done')
    }
  }

  if (!isOpen) return null

  // ════════════════════════════════════════════════════════════
  // PRESENTATION ONLY (case-entry-modals step 4) — ModalFrame + the mockup
  // (casevo-case-entry-modals.html 3a–3e). Everything above this line (parse,
  // map, match, validate, import, rollback, assign gate) is unchanged; the UI
  // below only reads that state and calls the same handlers.
  // ════════════════════════════════════════════════════════════

  // Display-only: which columns are still on Casevo's own suggestion (the same
  // pure autoMapHeaders call handleFile seeds `mapping` with) → "Auto" badge.
  const autoMapping: Record<number, string> = parsed ? autoMapHeaders(parsed.headers, ALL_FIELDS) : {}
  // Step indicator position: upload 0 · map 1 · match 2 · preview 3 · importing/done 4 (all complete).
  const STEP_LABELS = ['Upload', 'Map columns', 'Match clients', 'Review']
  const stepIndex = step === 'upload' ? 0 : step === 'map' ? 1 : step === 'match' ? 2 : step === 'preview' ? 3 : 4
  const skippedCount = builtRows.length - validIndexes.length
  const needsChoice = resolutions.filter((r) => r.ambiguous && r.action !== 'link').length
  const sampleOf = (col: number): string => {
    const vals: string[] = []
    for (const row of parsed?.rows ?? []) {
      const v = (row[col] ?? '').trim()
      if (v && !vals.includes(v)) vals.push(v)
      if (vals.length === 3) break
    }
    return vals.join(' · ')
  }

  const ic = (d: React.ReactNode, className = 'w-[15px] h-[15px]'): React.ReactNode => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className={className}>{d}</svg>
  )
  const IC_UPLOAD = <><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" /></>
  const IC_FILE = <><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /></>
  const IC_CHECK = <polyline points="20 6 9 17 4 12" />
  const IC_INFO = <><circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="12" /><line x1="12" y1="8" x2="12.01" y2="8" /></>
  const IC_ALERT = <><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></>
  const IC_LINK = <><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" /><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" /></>
  const IC_USERPLUS = <><path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="8.5" cy="7" r="4" /><line x1="20" y1="8" x2="20" y2="14" /><line x1="23" y1="11" x2="17" y2="11" /></>
  const IC_ARROW = <><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" /></>
  const IC_RIGHT = <polyline points="9 18 15 12 9 6" />

  // Mockup tokens.
  const btnPrimary = `${BTN_PRIMARY} text-sm rounded-lg px-5 py-2.5`
  const btnGhost = 'inline-flex items-center text-sm font-medium rounded-lg px-5 py-2.5 border border-casevo-border text-casevo-mid hover:border-casevo-mid hover:text-casevo-forest transition-colors focus:outline-none casevo-focus disabled:opacity-50'
  const BANNER = 'flex items-start gap-[10px] rounded-[10px] px-[13px] py-[10px] text-[12.5px] text-casevo-forest mb-[14px] bg-[color-mix(in_srgb,var(--casevo-mint)_45%,transparent)] border border-[color-mix(in_srgb,var(--casevo-brand)_25%,transparent)]'
  const ERR_BANNER = 'flex items-center justify-between gap-2 rounded-[10px] px-[13px] py-[10px] text-[12.5px] mb-[14px] bg-[#fbe9e6] text-[#c23b2c] dark:bg-[rgba(243,160,148,0.14)] dark:text-[#f3a094]'
  const TH = 'text-[10.5px] tracking-[0.14em] uppercase text-casevo-text-soft font-semibold text-left px-[12px] py-[8px] bg-casevo-input'
  const TD = 'px-[12px] py-[9px] border-t border-casevo-border-soft align-middle'
  const ERR_TXT = 'text-[12.5px] text-[#c23b2c] dark:text-[#f3a094]'
  const togBtn = (on: boolean, extra = ''): string =>
    `px-[11px] py-[6px] transition-colors focus:outline-none casevo-focus ${extra} ${on ? 'bg-casevo-mint text-casevo-brand dark:text-[#8fd6b0] font-semibold' : 'text-casevo-mid hover:text-casevo-forest'}`

  // ── Header: step indicator (headerSlot) ──
  const stepIndicator = (
    <ol className="flex items-center gap-[8px] mt-[2px]" aria-label="Import steps">
      {STEP_LABELS.map((label, i) => {
        const done = i < stepIndex
        const on = i === stepIndex
        return (
          <React.Fragment key={label}>
            {i > 0 && <li aria-hidden="true" className={`flex-1 min-w-[18px] h-[2px] rounded-[2px] ${i <= stepIndex ? 'bg-white/[0.85]' : 'bg-white/[0.22]'}`} />}
            <li className={`flex items-center gap-[8px] text-[12.5px] whitespace-nowrap ${on ? 'text-white font-semibold' : 'text-white/70'}`} aria-current={on ? 'step' : undefined}>
              <span className={`w-[22px] h-[22px] rounded-full grid place-items-center text-[11.5px] font-bold ${
                done ? 'bg-white/90 text-[#1f4a35]' : on ? 'bg-white text-[#1a3a2a] shadow-[0_0_0_3px_rgba(255,255,255,0.25)]' : 'bg-white/[0.12] border border-white/30'
              }`}>
                {done ? ic(IC_CHECK, "w-[12px] h-[12px]") : i + 1}
              </span>
              <span className={on ? '' : 'hidden sm:inline'}>{label}</span>
            </li>
          </React.Fragment>
        )
      })}
    </ol>
  )

  const SUBTITLE: Record<Step, string> = {
    upload: 'Create many cases at once from a spreadsheet export.',
    map: 'Tell Casevo which field each column holds.',
    match: 'Link rows to existing clients or create new ones.',
    preview: 'Check what will be created before importing.',
    importing: 'Importing — keep this window open until it finishes.',
    done: 'Your cases are ready in the case list.',
  }

  // ── Footer (left: Back/Cancel · right: the step's primary) ──
  const footerLeft = (
    <>
      {step === 'map' && <button type="button" className={btnGhost} onClick={() => setStep('upload')}>Back</button>}
      {step === 'match' && <button type="button" className={btnGhost} onClick={() => setStep('map')}>Back</button>}
      {step === 'preview' && <button type="button" className={btnGhost} onClick={() => setStep('match')}>Back</button>}
      {step === 'importing' && parsed && <span className="text-[12px] text-casevo-text-soft">Importing {counts.total} {counts.total === 1 ? 'case' : 'cases'} — all together or none…</span>}
      {step === 'done' && results?.failedRow && <button type="button" className={btnGhost} onClick={() => { setResults(null); setStep('preview') }}>Back to review</button>}
    </>
  )
  const footerRight = (
    <>
      {step === 'upload' && <button type="button" className={btnGhost} onClick={handleClose}>Cancel</button>}
      {step === 'map' && <button type="button" className={btnPrimary} disabled={mappedCount === 0} onClick={() => void goToMatch()}>Next: match clients{ic(IC_RIGHT)}</button>}
      {step === 'match' && <button type="button" className={btnPrimary} disabled={clientsLoading || !!matchError} onClick={() => setStep('preview')}>Next: review{ic(IC_RIGHT)}</button>}
      {step === 'preview' && <button type="button" className={btnPrimary} disabled={counts.total === 0 || mappedCount === 0} onClick={() => void runImport()}>{ic(IC_UPLOAD)}Import {counts.total} {counts.total === 1 ? 'case' : 'cases'}</button>}
      {step === 'done' && <button type="button" className={btnPrimary} onClick={handleClose}>Done</button>}
    </>
  )

  return (
    <>
      <ModalFrame
        onClose={handleClose}
        size="lg"
        // Close (✕ / Esc / backdrop) is blocked while rows are being created —
        // same rule as handleClose's own guard.
        dismissible={step !== 'importing'}
        eyebrow="Import"
        eyebrowIcon={ic(IC_UPLOAD, "w-[14px] h-[14px]")}
        pills={parsed && step !== 'upload' ? <span className={HERO_PILL_SOFT}>{fileName} · {parsed.rows.length} {parsed.rows.length === 1 ? 'row' : 'rows'}</span> : undefined}
        title={step === 'done' ? (results?.failedRow ? 'Nothing was imported' : 'Import complete') : 'Import cases from CSV'}
        subtitle={SUBTITLE[step]}
        headerSlot={stepIndicator}
        footerLeft={footerLeft}
        footerRight={footerRight}
      >
        {/* STEP 1 — UPLOAD */}
        {step === 'upload' && (
          <div>
            <div
              className="border-2 border-dashed border-casevo-border rounded-[14px] px-[20px] py-[34px] text-center bg-[color-mix(in_srgb,var(--casevo-mint)_30%,transparent)]"
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault()
                const f = e.dataTransfer.files?.[0]
                if (!f) return
                // Same filter as the file input's accept=".csv,text/csv" — a dropped
                // PDF/XLSX would otherwise be decoded as text into garbage columns.
                if (!/\.csv$/i.test(f.name) && f.type !== 'text/csv') { setParseError('Please drop a .csv file (export it from Excel as “CSV”).'); return }
                void handleFile(f)
              }}
            >
              <div className="w-[52px] h-[52px] mx-auto mb-[12px] rounded-[16px] bg-casevo-mint text-casevo-brand dark:text-[#8fd6b0] grid place-items-center">
                {ic(IC_UPLOAD, "w-[24px] h-[24px]")}
              </div>
              <p className="text-[15px] font-bold text-casevo-forest">Drop a CSV file here</p>
              <p className="text-[12.5px] text-casevo-text-soft mt-[6px] mb-[14px]">or choose one from your computer. The first row must be the column headers.</p>
              <label className={`${btnPrimary} cursor-pointer`}>
                {ic(IC_FILE)}Choose CSV file
                <input type="file" accept=".csv,text/csv" className="sr-only" onChange={(e) => { const f = e.target.files?.[0]; if (f) void handleFile(f) }} />
              </label>
              {parseError && <p role="alert" className={`${ERR_TXT} mt-[14px]`}>{parseError}</p>}
            </div>
            <div className="grid sm:grid-cols-3 gap-[10px] mt-[16px]">
              {[
                ['Mixed clients are fine', 'Rows are matched to existing clients by name. New names become new clients.'],
                ['Any column order', 'Casevo suggests a field for each column. You confirm or change it in the next step.'],
                ['Excel exports work', 'UTF-8 and Windows “CSV (Comma delimited)” files are both read correctly.'],
              ].map(([t, d]) => (
                <div key={t} className="border border-casevo-border-soft rounded-[12px] px-[13px] py-[11px] text-[12px] text-casevo-text-soft leading-[1.45]">
                  <b className="block text-casevo-forest text-[12.5px] mb-[2px]">{t}</b>{d}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* STEP 2 — MAP COLUMNS (always client + case fields) */}
        {step === 'map' && parsed && (
          <div>
            <div className={BANNER}>
              {ic(IC_INFO, "w-[16px] h-[16px] text-casevo-brand shrink-0 mt-[1px]")}
              <span>Map <b>client forename</b> and <b>surname</b> so rows can be matched to existing clients. <b>{mappedCount} of {parsed.headers.length}</b> columns mapped.</span>
            </div>
            <div className="border border-casevo-border rounded-[12px] overflow-hidden">
              <table className="w-full border-collapse text-[13px]">
                <thead><tr><th className={TH}>CSV column · sample</th><th className={TH} aria-label="maps to" /><th className={TH}>Casevo field</th></tr></thead>
                <tbody>
                  {parsed.headers.map((h, i) => {
                    const key = mapping[i] ?? ''
                    const field = key ? FIELD_BY_KEY.get(key) : undefined
                    const isAuto = !!key && autoMapping[i] === key
                    return (
                      <tr key={i}>
                        <td className={`${TD} max-w-[260px]`}>
                          <div className="font-semibold text-casevo-forest truncate">{h || <span className="italic font-normal text-casevo-text-soft">(column {i + 1})</span>}</div>
                          <div className="text-[12px] text-casevo-text-soft truncate">{sampleOf(i) || '—'}</div>
                        </td>
                        <td className={`${TD} w-[28px] text-casevo-text-soft`}>{ic(IC_ARROW, "w-[16px] h-[16px]")}</td>
                        <td className={TD}>
                          <div className="flex items-center gap-[8px]">
                            {field && <span className="text-[10px] font-bold tracking-[0.06em] uppercase text-casevo-text-soft border border-casevo-border-soft px-[5px] py-[1px] rounded-[4px] shrink-0">{CLIENT_KEYS.has(key) ? 'Client' : 'Case'}</span>}
                            <select
                              value={key}
                              onChange={(e) => setMapping((m) => ({ ...m, [i]: e.target.value }))}
                              aria-label={`Casevo field for ${h || `column ${i + 1}`}`}
                              className={`min-w-[200px] flex-1 sm:flex-none text-[13px] rounded-lg px-[10px] py-[6px] text-casevo-forest focus:outline-none focus:border-casevo-mid casevo-focus ${
                                key ? 'border border-casevo-border bg-casevo-input dark:bg-[#1f2e26]' : 'border border-dashed border-casevo-border bg-transparent text-casevo-text-soft'
                              }`}
                            >
                              <option value="">Don’t import</option>
                              <optgroup label="Client fields">
                                {CLIENT_IMPORT_FIELDS.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
                              </optgroup>
                              <optgroup label="Case fields">
                                {CASE_IMPORT_FIELDS.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
                              </optgroup>
                            </select>
                            {isAuto && <span className="text-[10px] font-bold tracking-[0.08em] uppercase text-casevo-brand dark:text-[#8fd6b0] bg-casevo-mint px-[6px] py-[2px] rounded-[4px] shrink-0">Auto</span>}
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
            {mappedCount === 0 && <p className={`${ERR_TXT} mt-[10px]`}>Map at least one column to continue.</p>}
          </div>
        )}

        {/* STEP 3 — MATCH CLIENTS */}
        {step === 'match' && parsed && (
          <div>
            <div className={BANNER}>
              {ic(IC_INFO, "w-[16px] h-[16px] text-casevo-brand shrink-0 mt-[1px]")}
              <span>Names matching one existing client are linked automatically. Names with <b>more than one</b> match are never auto-linked — pick the right client, or leave them as new. A case is always created for every row.</span>
            </div>
            {matchError && (
              <div role="alert" className={ERR_BANNER}>
                <span>{matchError}</span>
                <button type="button" onClick={() => void goToMatch()} className="underline whitespace-nowrap font-semibold">Retry</button>
              </div>
            )}
            {clientsLoading ? (
              <div className="flex items-center gap-2 text-sm text-casevo-mid py-6">
                <div className="h-4 w-4 rounded-full border-2 border-casevo-mint border-t-casevo-brand animate-spin" />
                Loading existing clients to match against…
              </div>
            ) : resolutions.length > 0 && (
              <>
                <div className="flex gap-[20px] text-[12px] text-casevo-text-soft -mt-[4px] mb-[14px] flex-wrap">
                  <span><b className="text-casevo-brand dark:text-[#8fd6b0]">{resolutions.filter((r) => r.action === 'link').length}</b> linked to existing</span>
                  <span><b className="text-casevo-forest">{resolutions.filter((r) => r.action === 'create').length}</b> new clients</span>
                  {needsChoice > 0 && <span><b className="text-[#8a6212] dark:text-[#dcbb72]">{needsChoice}</b> with several possible matches</span>}
                </div>
                <div className="border border-casevo-border rounded-[12px] overflow-hidden">
                  {builtRows.map((r, i) => {
                    const res = resolutions[i]
                    if (!res) return null
                    const name = `${r.forename} ${r.surname}`.trim() || `(row ${i + 2}, no name)`
                    const linkedName = res.matchedClientName ?? 'existing client'
                    return (
                      <div key={i} className="flex items-center gap-[12px] px-[14px] py-[11px] border-t border-casevo-border-soft first:border-t-0">
                        <span className="font-mono text-[11.5px] text-casevo-text-soft w-[30px] shrink-0">{i + 2}</span>
                        <div className="flex-1 min-w-0">
                          <div className="truncate">
                            <b className="text-[14px] text-casevo-forest">{name}</b>
                            {res.ambiguous && <span className="ml-2 text-[10px] font-bold tracking-[0.06em] uppercase px-[6px] py-[2px] rounded-[4px] bg-[#fdf3dc] text-[#8a6212] dark:bg-[rgba(214,170,80,0.14)] dark:text-[#dcbb72]">Multiple matches</span>}
                          </div>
                          <div className={`text-[12.5px] mt-[2px] truncate ${res.action === 'link' ? 'text-casevo-brand dark:text-[#8fd6b0]' : 'text-casevo-text-soft'}`}>
                            {res.action === 'link' ? (
                              <>{ic(IC_LINK, "w-[13px] h-[13px] inline -mt-[2px] mr-[5px]")}Existing client: <b>{linkedName}</b>{' '}
                                <button type="button" onClick={() => setManualPickRow(i)} className="underline focus:outline-none casevo-focus rounded">change</button></>
                            ) : res.ambiguous ? (
                              <>{ic(IC_ALERT, "w-[13px] h-[13px] inline -mt-[2px] mr-[5px]")}A new client will be created — or choose which existing client this is</>
                            ) : (
                              <>{ic(IC_USERPLUS, "w-[13px] h-[13px] inline -mt-[2px] mr-[5px]")}A new client will be created</>
                            )}
                          </div>
                        </div>
                        {/* New-vs-Existing CLIENT choice — same handlers as before. */}
                        <div className="shrink-0 inline-flex rounded-lg border border-casevo-border overflow-hidden text-[12px]" role="group" aria-label={`Client for row ${i + 2}`}>
                          <button type="button" aria-pressed={res.action === 'create'} onClick={() => setResolution(i, { action: 'create', clientId: undefined })} className={togBtn(res.action === 'create')}>New client</button>
                          <button
                            type="button"
                            aria-pressed={res.action === 'link'}
                            onClick={() => {
                              if (res.matchedClientId) setResolution(i, { action: 'link', clientId: res.matchedClientId })
                              else if (res.clientId) setResolution(i, { action: 'link', clientId: res.clientId })
                              else setManualPickRow(i) // no match → must choose an existing client
                            }}
                            className={togBtn(res.action === 'link', 'border-l border-casevo-border')}
                          >
                            Existing client
                          </button>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </>
            )}
          </div>
        )}

        {/* STEP 4 — REVIEW (preview / validate) */}
        {step === 'preview' && parsed && (
          <div>
            {ambiguousColumns.map((a) => (
              // (Audit fix batch 2) The shared note shell; the choice sits in a
              // plain white section beneath the facts (no inputs on a tint).
              <ActionBox key={a.key} className="mb-[12px]" ariaLabel={`Two-digit years in ${a.label}`} title={`${a.label}: which century?`}
                message={<>{a.rows === 1 ? 'One row has' : `${a.rows} rows have`} a 2-digit year (e.g. “{a.sample}”).{!centuries[a.key] && ' Those rows are skipped until you choose.'}</>}
                form={
                  <fieldset className="m-0 p-0 border-0">
                    <legend className="sr-only">Two-digit years in {a.label}</legend>
                    <div className="flex gap-[16px] text-[13px] text-casevo-forest">
                      {([1900, 2000] as Century[]).map((c) => (
                        <label key={c} className="inline-flex items-center gap-[6px] cursor-pointer">
                          <input type="radio" name={`century-${a.key}`} className="accent-casevo-primary" checked={centuries[a.key] === c} onChange={() => setCenturies((x) => ({ ...x, [a.key]: c }))} />
                          {c === 1900 ? '19xx (e.g. 1927)' : '20xx (e.g. 2027)'}
                        </label>
                      ))}
                    </div>
                  </fieldset>
                } />
            ))}
            {warningRows > 0 && (
              <p className="mb-[12px] text-[12.5px] text-casevo-warn">{warningRows === 1 ? 'One row has' : `${warningRows} rows have`} date warnings (shown below) — they still import; check them afterwards.</p>
            )}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-[10px] mb-[16px]">
              {([
                [counts.total, counts.total === 1 ? 'case to create' : 'cases to create', false],
                [counts.create, counts.create === 1 ? 'new client' : 'new clients', false],
                [counts.link, 'linked to existing', false],
                [skippedCount, skippedCount === 1 ? 'row skipped' : 'rows skipped', skippedCount > 0],
              ] as const).map(([n, label, err]) => (
                <div key={label} className="border border-casevo-border rounded-[12px] px-[14px] py-[12px]">
                  <b className={`block text-[24px] font-bold tracking-[-0.01em] ${err ? 'text-[#c23b2c] dark:text-[#f3a094]' : 'text-casevo-forest'}`}>{n}</b>
                  <span className="text-[12px] text-casevo-text-soft">{label}</span>
                </div>
              ))}
            </div>
            <div className="border border-casevo-border rounded-[12px] overflow-hidden">
              <table className="w-full border-collapse text-[13px]">
                <thead><tr><th className={TH}>Row</th><th className={TH}>Name</th><th className={TH}>Client</th><th className={TH}>Case / notes</th></tr></thead>
                <tbody>
                  {builtRows.slice(0, 6).map((r, i) => {
                    const res = resolutions[i]
                    const errs = rowError(i)
                    return (
                      <tr key={i}>
                        <td className={`${TD} font-mono text-[11.5px] text-casevo-text-soft`}>{i + 2}</td>
                        <td className={`${TD} text-casevo-forest`}>{`${r.forename} ${r.surname}`.trim() || '—'}</td>
                        <td className={`${TD} text-casevo-forest`}>{res?.action === 'link' ? 'Existing client' : 'New client'}</td>
                        <td className={`${TD} text-casevo-text-soft`}>
                          {errs.length > 0 ? <span className={ERR_TXT}>{errs.join('; ')} — row skipped.</span> : String(r.caseUpdates.caseName ?? `${Object.keys(r.caseUpdates).length} field(s)`)}
                          {errs.length === 0 && dateRulesFor(i).warnings.length > 0 && <span className="block text-[12px] text-casevo-warn mt-[2px]">{dateRulesFor(i).warnings.join(' ')}</span>}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
            {parsed.rows.length > 6 && <p className="text-[12px] text-casevo-text-soft mt-[8px]">…and {parsed.rows.length - 6} more rows.</p>}
            {/* Claim type (B2) — required, one for every case in the file. */}
            <div className="px-[14px] py-[12px] border border-casevo-border rounded-[12px] mt-[14px]">
              <ClaimTypeFields
                idPrefix="imp"
                value={claim}
                onChange={(next) => { setClaim(next); if (next.claimType) setClaimError(null) }}
                error={claimError}
              />
              <p className="text-[12px] text-casevo-text-soft mt-[6px]">Used for every case in this file. You can change it on each case afterwards.</p>
            </div>
            {/* Assign — default ON. Only users who can see every case (admin /
                seeAllCases) may untick; for anyone else an unassigned case
                would vanish from their view (same gate as before). */}
            {canLeaveUnassigned ? (
              <label className="flex items-center gap-[10px] px-[14px] py-[12px] border border-casevo-border rounded-[12px] mt-[14px] text-[13.5px] text-casevo-forest cursor-pointer select-none">
                <input type="checkbox" checked={assignToMe} onChange={(e) => setAssignToMe(e.target.checked)} className="h-[18px] w-[18px] accent-casevo-brand shrink-0" />
                <span>Assign imported cases to me<small className="block text-[12px] text-casevo-text-soft">Leave unticked to import them unassigned (you can see all cases, so they won’t disappear from your view).</small></span>
              </label>
            ) : (
              <div className="flex items-center gap-[10px] px-[14px] py-[12px] border border-casevo-border rounded-[12px] mt-[14px] text-[13.5px] text-casevo-forest">
                <span className="w-[18px] h-[18px] rounded-[5px] bg-casevo-primary text-white grid place-items-center shrink-0">{ic(IC_CHECK, "w-[12px] h-[12px]")}</span>
                <span>Imported cases will be assigned to you<small className="block text-[12px] text-casevo-text-soft">So they stay visible to you after the import.</small></span>
              </div>
            )}
            {mappedCount === 0 && <p className={`${ERR_TXT} mt-[10px]`}>Map at least one column before importing.</p>}
          </div>
        )}

        {/* STEP 5 — IMPORTING (close blocked: dismissible={false}) */}
        {step === 'importing' && parsed && (
          <div className="text-center py-[10px]" aria-live="polite">
            <div className="h-6 w-6 mx-auto rounded-full border-2 border-casevo-mint border-t-casevo-brand animate-spin mb-[12px]" />
            <p className="text-[15px] font-bold text-casevo-forest">Importing… {progress} / {parsed.rows.length}</p>
            <div className="h-[8px] rounded-[5px] bg-casevo-border-soft overflow-hidden mt-[18px] mb-[6px]" role="progressbar" aria-valuemin={0} aria-valuemax={parsed.rows.length} aria-valuenow={progress}>
              <i className="block h-full bg-casevo-brand transition-[width] duration-200" style={{ width: `${parsed.rows.length ? (progress / parsed.rows.length) * 100 : 0}%` }} />
            </div>
            <p className="text-[12px] text-casevo-text-soft">Please keep this window open — closing is disabled until it finishes.</p>
          </div>
        )}

        {/* DONE */}
        {step === 'done' && results && parsed && (
          <div>
            <div className="text-center pt-[10px] pb-[4px]">
              {results.failedRow ? (
                <>
                  <h4 className="text-[18px] font-bold text-casevo-forest">Nothing was imported</h4>
                  <p role="alert" className={`${ERR_TXT} text-[13.5px] mt-[8px]`}>
                    {results.failedRow.row !== null ? <><b>Row {results.failedRow.row}</b> failed: {results.failedRow.reason}</> : results.failedRow.reason}
                  </p>
                  <p className="text-[12.5px] text-casevo-text-soft mt-[6px]">The import is all or nothing, so no cases or clients were created. Fix that row (or leave it out) and import again.</p>
                </>
              ) : (
                <>
                  <div className="w-[56px] h-[56px] mx-auto mb-[12px] rounded-full bg-casevo-mint text-casevo-brand dark:text-[#8fd6b0] grid place-items-center">
                    {ic(IC_CHECK, "w-[28px] h-[28px]")}
                  </div>
                  <h4 className="text-[18px] font-bold text-casevo-forest">Imported {results.success} {results.success === 1 ? 'case' : 'cases'}</h4>
                  {results.failures.length > 0 && <p className={`${ERR_TXT} text-[13px] mt-[6px]`}>{results.failures.length} {results.failures.length === 1 ? 'row was' : 'rows were'} left out</p>}
                </>
              )}
            </div>
            {results.failures.length > 0 && (
              <div className="mt-[16px] border border-casevo-border rounded-[12px] max-h-48 overflow-y-auto">
                {results.failures.map((f, i) => (
                  <div key={i} className="px-[14px] py-[9px] border-t border-casevo-border-soft first:border-t-0 text-[12.5px] text-casevo-text-soft"><b className="text-casevo-forest">Row {f.row}:</b> {f.error}</div>
                ))}
              </div>
            )}
          </div>
        )}
      </ModalFrame>

      {/* Manual "link to existing" picker (reuses ClientPickerModal). Wrapped
          in its own stacking context so it sits ABOVE the ModalFrame (z-70)
          — ClientPickerModal's overlay is z-50. */}
      {manualPickRow !== null && (
        <div className="relative z-[80]">
          <ClientPickerModal
            isOpen
            mode="pick"
            onClose={() => setManualPickRow(null)}
            onClientSelected={(clientId) => {
              const c = clients.find((x) => x.id === clientId)
              setResolution(manualPickRow, {
                action: 'link',
                clientId,
                matchedClientName: c ? clientName(c) : 'selected client',
              })
              setManualPickRow(null)
            }}
          />
        </div>
      )}
    </>
  )
}

export default ImportCasesModal
